/**
 * Composite access rules shared by the portal nav (BusinessPortalLayout) and
 * the pages it leads to. One copy so the nav can never show a tab whose page
 * bounces, or hide one that works.
 *
 * Presentation only — every surface's resolvers re-derive the grant.
 */
import type { PermissionChecker } from './permissions'

/**
 * The legacy /metrics dashboard audience — metrics is the ONLY portal surface
 * these minted roles can open, so metrics gating must admit them everywhere it
 * appears. Excluding them while the route admitted them once created an
 * infinite bounce: metrics → policies → login → metrics.
 */
export const METRICS_VIEWER_ROLES: string[] = ['MetricsSender']

/**
 * Only list-kind allowed-metrics choices are real viewer grants
 * (AuthzAccessEntry-backed owner-group lists). PROPERTY_MANAGER choices are
 * minted for anyone the PM's domain config allows — the API's per-PM metrics
 * path does not accept them, so neither may any surface gate. Every
 * hasMetricsChoices computation must count choices through this filter.
 */
export const isMetricsViewerChoice = (choice: { kind: string }) =>
  choice.kind !== 'PROPERTY_MANAGER'

interface AccessArgs {
  hasRole: (roles: string | string[]) => boolean
  can: PermissionChecker['can']
}

/**
 * Workspace members via the metrics grant, plus admins and MetricsSender —
 * and owner-group viewers.
 *
 * Owner-group access is deliberately NOT a role. It lives in AuthzAccessEntry
 * and is resolved on the dashboard path by getAllowedMetricsChoices, so
 * callers pass that result in as `hasMetricsChoices` rather than having core
 * auth scan the table on every request. Presentation only — every metrics
 * resolver still re-derives the grant through getPmScope.
 */
export const canAccessMetrics = ({
  hasRole,
  can,
  hasMetricsChoices = false,
}: AccessArgs & { hasMetricsChoices?: boolean }) =>
  hasRole(['Admin', 'Super', ...METRICS_VIEWER_ROLES]) ||
  can('metrics', 'read') ||
  hasMetricsChoices

/**
 * PMs with a centralized-monitor surface, and who may see it. Mirrors
 * MONITOR_PMS in api/src/lib/centralizedMonitor.ts — presentation only, the
 * service re-derives this. Keep the two in step: a PM listed here but not
 * there gets a nav tab whose page errors.
 *
 * `internalOnly` is for a PM whose rollout is still in flight, where the
 * numbers aren't yet something we show the customer.
 */
export const MONITOR_PM_AUDIENCE: Record<string, 'pmVisible' | 'internalOnly'> =
  {
    rpm: 'pmVisible',
    mg: 'internalOnly',
  }

/**
 * Only on PMs that have a monitor surface — even for Two Dots admins, since
 * the page renders a not-available notice elsewhere. The monitor has no grant
 * of its own: on a pmVisible PM its members come in via the metrics grant
 * (workspace members only — the legacy MetricsSender/owner-group audiences
 * stay dashboard-only). On an internalOnly PM there is no member path at all.
 */
export const canAccessMonitor = ({
  hasRole,
  can,
  currentPm,
}: AccessArgs & { currentPm: string | null }) => {
  const audience =
    currentPm == null ? undefined : MONITOR_PM_AUDIENCE[currentPm]
  if (!audience) return false
  if (hasRole(['Admin', 'Super'])) return true
  return audience === 'pmVisible' && can('metrics', 'read')
}

export const canAccessAgent = ({
  agentEnabled,
  hasRole,
  can,
}: AccessArgs & { agentEnabled: boolean }) =>
  agentEnabled && (hasRole(['Admin', 'Super']) || can('agent', 'read'))
